import { useQuery } from '@tanstack/react-query';
import {
  type DocStatus,
  type GoodsIssue,
  type GoodsReceipt,
  type InventoryDocLine,
  type LowStockAlert,
  type StockCount,
  type StockCountLine,
  type StockTransferDoc,
} from '@/services/inventory.types';
import { inventoryService } from '@/services/inventory.service';
import { reconcileReceiptLineTotals } from '@/utils/inventoryReceiptValues';
import type {
  GoodsIssueListQuery,
  GoodsReceiptListQuery,
  StockTakeListQuery,
  StockTransferListQuery,
} from '@/services/inventory.service';
import { formatSystemUser } from '@/utils/system-user';
import type { SyncBadge } from '@/services/sales-invoice.service';
import { isSyncDocType, type SyncDocType } from '@/modules/accounting-sync/hooks/useSyncActions';

function unwrapList<T>(r: unknown): T[] {
  if (Array.isArray(r)) return r as T[];
  if (r && typeof r === 'object') {
    const obj = r as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (obj.data && typeof obj.data === 'object') {
      const nested = obj.data as Record<string, unknown>;
      if (Array.isArray(nested.data)) return nested.data as T[];
    }
  }
  return [];
}

function unwrapPagedList<T>(r: unknown): { data: T[]; total: number } {
  if (Array.isArray(r)) return { data: r as T[], total: r.length };
  if (!r || typeof r !== 'object') return { data: [], total: 0 };

  const root = r as Record<string, unknown>;
  const nested = root.data && typeof root.data === 'object' && !Array.isArray(root.data)
    ? root.data as Record<string, unknown>
    : undefined;
  const data = Array.isArray(root.data)
    ? root.data as T[]
    : Array.isArray(nested?.data)
      ? nested.data as T[]
      : [];
  const pagination = (root.pagination ?? root.meta ?? nested?.pagination ?? nested?.meta) as
    | Record<string, unknown>
    | undefined;
  const rawTotal = root.total ?? nested?.total ?? pagination?.total;
  const total = typeof rawTotal === 'number' && Number.isFinite(rawTotal)
    ? rawTotal
    : data.length;
  return { data, total };
}

function unwrapItem<T>(r: unknown): T | null {
  if (!r) return null;
  if (typeof r === 'object') {
    const obj = r as Record<string, unknown>;
    if ('data' in obj && obj.data && typeof obj.data === 'object') {
      return obj.data as T;
    }
    return r as T;
  }
  return null;
}

function asString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object' && value !== null && 'toString' in value) {
    const s = String(value);
    return s === '[object Object]' ? undefined : s;
  }
  return undefined;
}

function relatedId(value: unknown): string | undefined {
  const direct = asString(value);
  if (direct) return direct;
  if (!value || typeof value !== 'object') return undefined;
  const related = value as Record<string, unknown>;
  return asString(related._id ?? related.id ?? related.customer_id);
}

function asNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function asDisplayCode(value: unknown): string | undefined {
  const code = asString(value)?.trim();
  if (!code || /^[a-f0-9]{24}$/i.test(code)) return undefined;
  return code;
}

const SYSTEM_CREATED_LABEL = 'Hệ thống tạo tự động';

function actorName(...values: unknown[]): string {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const name = formatSystemUser(value, '').trim();
    if (name) return name;
  }
  return SYSTEM_CREATED_LABEL;
}

function asBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value === 'true') return true;
    if (value === 'false') return false;
  }
  return undefined;
}

function normalizeSerials(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object') {
            const obj = item as Record<string, unknown>;
            return asString(obj.serial ?? obj.serial_number ?? obj.serialNo ?? obj.code);
          }
          return asString(item);
        })
        .filter((serial): serial is string => Boolean(serial?.trim()))
        .map((serial) => serial.trim()),
    ),
  );
}

function normalizeReceiptItem(raw: unknown): InventoryDocLine {
  const item = (raw ?? {}) as Record<string, unknown>;
  return {
    itemId: asString(item._id ?? item.id ?? item.item_id ?? item.itemId),
    product_id: asString(item.product_id ?? item.productId),
    variant_id: asString(item.variant_id ?? item.variantId),
    warehouse_id: asString(item.warehouse_id ?? item.warehouseId),
    warehouse_name: (item.warehouse_name ?? item.warehouseName) as string | undefined,
    from_warehouse_id: asString(item.from_warehouse_id ?? item.fromWarehouseId),
    from_warehouse_name: asString(item.from_warehouse_name ?? item.fromWarehouseName),
    to_warehouse_id: asString(item.to_warehouse_id ?? item.toWarehouseId),
    to_warehouse_name: asString(item.to_warehouse_name ?? item.toWarehouseName),
    sku: (item.product_sku ?? item.product_code ?? item.sku ?? item.variant_code ?? '') as string,
    variant_code: (item.variant_code ?? item.variantCode) as string | undefined,
    product_name: (item.product_name ?? item.productName ?? item.name ?? item.description ?? '') as string,
    karat: (item.karat ?? item.purity ?? '') as string,
    tracking_mode: ((item.tracking_mode ?? item.trackingMode) as 'quantity' | 'serial' | undefined) ?? 'quantity',
    trackingMode: ((item.tracking_mode ?? item.trackingMode) as 'quantity' | 'serial' | undefined) ?? 'quantity',
    qty: Number(item.quantity ?? item.qty ?? 0),
    serials: normalizeSerials(item.serials ?? item.serial_numbers ?? item.serialNumbers),
    weight_gram: Number(item.weight_g ?? item.weightGram ?? 0),
    weightGram: Number(item.weight_g ?? item.weightGram ?? 0),
    unit_price: item.unit_price !== null && item.unit_price !== undefined
      ? Number(item.unit_price)
      : item.unit_cost !== null && item.unit_cost !== undefined
        ? Number(item.unit_cost)
        : undefined,
    unit_cost: item.unit_cost === null || item.unit_cost === undefined ? undefined : Number(item.unit_cost),
    cost_amount: item.cost_amount === null || item.cost_amount === undefined ? undefined : Number(item.cost_amount),
    amount: item.amount === null || item.amount === undefined ? undefined : Number(item.amount),
    note: (item.note) as string | undefined,
    lot_number: (item.lot_number ?? item.lotNumber) as string | undefined,
    supplier_lot_number: (item.supplier_lot_number ?? item.supplierLotNumber) as string | undefined,
    expiry_date: (item.expiry_date ?? item.expiryDate) as string | undefined,
    warehouse_location_code: (item.warehouse_location_code ?? item.warehouseLocationCode) as string | undefined,
    warehouse_location_id: asString(item.warehouse_location_id ?? item.warehouseLocationId),
    uom_code: (item.uom_code ?? item.uomCode) as string | undefined,
    debit_account_code: (item.debit_account_code ?? item.debitAccountCode) as string | undefined,
    credit_account_code: (item.credit_account_code ?? item.creditAccountCode) as string | undefined,
    source_line_id: asString(item.source_line_id ?? item.sourceLineId ?? item._id ?? item.id),
    sales_invoice_id: asString(item.sales_invoice_id ?? item.salesInvoiceId),
    sales_invoice_number: asString(item.sales_invoice_number ?? item.salesInvoiceNumber),
    sold_quantity: item.sold_quantity === null || item.sold_quantity === undefined ? undefined : Number(item.sold_quantity),
    issued_before_quantity: item.issued_before_quantity === null || item.issued_before_quantity === undefined ? undefined : Number(item.issued_before_quantity),
};
}

function normalizeInventoryItems(raw: Record<string, unknown>): InventoryDocLine[] {
  const candidates = [
    raw.items,
    raw.lines,
    raw.receipt_items,
    raw.receiptItems,
    raw.goods_receipt_items,
    raw.goodsReceiptItems,
  ];
  const rows = candidates.find(Array.isArray);
  return Array.isArray(rows) ? rows.map(normalizeReceiptItem) : [];
}

function normalizeDocStatus(raw: unknown): DocStatus {
  const status = asString(raw) ?? 'draft';
  if (status === 'pending_approval' || status === 'submitted') return 'pending';
  if (
    status === 'draft' ||
    status === 'pending' ||
    status === 'reviewing' ||
    status === 'approved' ||
    status === 'in_transit' ||
    status === 'completed' ||
    status === 'rejected' ||
    status === 'cancelled'
  ) {
    return status;
  }
  return 'draft';
}

export function normalizeReceipt(raw: Record<string, unknown>): GoodsReceipt {
  const items = normalizeInventoryItems(raw);
  const totalQty = (raw.total_quantity ?? raw.totalQty) as number | undefined;
  const totalValue = (raw.total_value ?? raw.totalValue) as number | undefined;
  const resolvedTotalValue = totalValue ?? items.reduce(
    (sum, item) => sum + item.qty * (item.unit_price ?? 0),
    0,
  );
  const normalizedLineTotals = reconcileReceiptLineTotals(items, resolvedTotalValue);
  const lines = items.map((item, index) => ({
    ...item,
    lineTotal: normalizedLineTotals[index] ?? 0,
  }));
  return {
    _id: asString(raw._id) ?? '',
    code: (raw.receipt_code ?? raw.code ?? '') as string,
    refType: (raw.receipt_type ?? raw.refType ?? 'purchase') as GoodsReceipt['refType'],
    refCode: (raw.reference_code ?? raw.refCode ?? raw.source_code ?? raw.sourceCode) as string | undefined,
    purchaseOrderId: asString(raw.purchase_order_id ?? raw.purchaseOrderId),
    sourceType: asString(raw.source_type ?? raw.sourceType),
    sourceId: asString(raw.source_id ?? raw.sourceId),
    sourceCode: asString(raw.source_code ?? raw.sourceCode),
    systemGenerated: Boolean(raw.system_generated ?? raw.systemGenerated ?? raw.source_type === 'stock_transfer'),
    documentOrigin: asString(raw.document_origin ?? raw.documentOrigin),
    transferId: asString(raw.transfer_id ?? raw.transferId ?? (raw.source_type === 'stock_transfer' ? raw.source_id : undefined)),
    journalEntryId: asString(raw.journal_entry_id ?? raw.journalEntryId),
    journalEntryNumber: asDisplayCode(raw.journal_entry_number ?? raw.journalEntryNumber ?? raw.entry_number ?? raw.entryNumber),
    supplierName: (raw.supplier_name ?? raw.supplierName) as string | undefined,
    warehouseId: asString(raw.warehouse_id ?? raw.warehouseId),
    lines,
    totalQty: totalQty ?? items.reduce((s, i) => s + i.qty, 0),
    totalValue: resolvedTotalValue,
    storeId: asString(raw.store_id ?? raw.storeId) ?? '',
    storeCode: asString(raw.store_code ?? raw.storeCode),
    storeName: (raw.store_name ?? raw.storeName ?? '') as string,
    staffName: actorName(raw.staff_name, raw.staffName, raw.created_by_name, raw.createdByName, raw.created_by, raw.createdBy),
    status: normalizeDocStatus(raw.status),
    rawStatus: asString(raw.status),
    note: (raw.note) as string | undefined,
    createdAt: (raw.created_at ?? raw.createdAt ?? '') as string,
    completedAt: (raw.completed_at ?? raw.completedAt) as string | undefined,
    documentDate: (raw.document_date ?? raw.receipt_date) as string | undefined,
    accountingDate: raw.accounting_date as string | undefined,
    documentNo: (raw.document_no ?? raw.receipt_code) as string | undefined,
    supplierCode: raw.supplier_code as string | undefined,
    supplierAddress: raw.supplier_address as string | undefined,
    deliveryPerson: raw.delivery_person as string | undefined,
    sync: raw.sync as GoodsReceipt['sync'],
    sync_state: raw.sync_state as GoodsReceipt['sync_state'],
    amis_sync_blocked_reason: asString(raw.amis_sync_blocked_reason),
  };
}

export function normalizeIssue(raw: Record<string, unknown>): GoodsIssue {
  const items = normalizeInventoryItems(raw);
  const purposeRaw = (raw.purpose ?? '') as string;
  const purpose: GoodsIssue['purpose'] =
    purposeRaw === 'adjust'
      ? 'adjustment'
      : (purposeRaw as GoodsIssue['purpose']) || 'workshop';
  return {
    _id: asString(raw._id) ?? '',
    code: (raw.issue_code ?? raw.code ?? '') as string,
    purpose,
    warehouseId: asString(raw.warehouse_id ?? raw.warehouseId),
    lines: items,
    totalQty: Number(raw.total_quantity ?? raw.totalQty ?? items.reduce((s, i) => s + i.qty, 0)),
    totalValue: Number(raw.total_value ?? raw.totalValue ?? items.reduce((s, i) => s + i.qty * (i.unit_price ?? 0), 0)),
    storeId: asString(raw.store_id ?? raw.storeId) ?? '',
    storeCode: asString(raw.store_code ?? raw.storeCode),
    storeName: (raw.store_name ?? raw.storeName ?? '') as string,
    staffName: actorName(raw.staff_name, raw.staffName, raw.created_by_name, raw.createdByName, raw.created_by, raw.createdBy),
    status: normalizeDocStatus(raw.status),
    rawStatus: asString(raw.status),
    note: (raw.note) as string | undefined,
    createdAt: (raw.created_at ?? raw.createdAt ?? '') as string,
    completedAt: (raw.completed_at ?? raw.completedAt ?? raw.approved_at ?? raw.approvedAt) as
      | string
      | undefined,
    documentDate: (raw.document_date ?? raw.issue_date) as string | undefined,
    accountingDate: raw.accounting_date as string | undefined,
    documentNo: (raw.document_no ?? raw.issue_code) as string | undefined,
    sourceType: asString(raw.source_type ?? raw.sourceType),
    sourceId: asString(raw.source_id ?? raw.sourceId),
    sourceCode: asString(raw.source_code ?? raw.sourceCode),
    systemGenerated: Boolean(raw.system_generated ?? raw.systemGenerated ?? raw.source_type === 'stock_transfer'),
    documentOrigin: asString(raw.document_origin ?? raw.documentOrigin),
    transferId: asString(raw.transfer_id ?? raw.transferId ?? (raw.source_type === 'stock_transfer' ? raw.source_id : undefined)),
    orderNumber: asString(raw.order_number ?? raw.orderNumber),
    journalEntryId: asString(raw.journal_entry_id ?? raw.journalEntryId),
    journalEntryNumber: asDisplayCode(raw.journal_entry_number ?? raw.journalEntryNumber ?? raw.entry_number ?? raw.entryNumber),
    costingStatus: raw.costing_status === null
      ? null
      : asString(raw.costing_status ?? raw.costingStatus),
    valuationMethod: raw.valuation_method === null
      ? null
      : asString(raw.valuation_method ?? raw.valuationMethod),
    customerId: relatedId(raw.customer_id ?? raw.customerId ?? raw.customer),
    customerCode: raw.customer_code as string | undefined,
    customerName: raw.customer_name as string | undefined,
    customerAddress: raw.customer_address as string | undefined,
    customerPhone: asString(raw.customer_phone ?? raw.receiver_phone ?? raw.phone),
    customerTaxCode: asString(raw.customer_tax_code ?? raw.tax_code ?? raw.tax_id),
    receiverName: raw.receiver_name as string | undefined,
    salesperson: raw.salesperson as string | undefined,
    deliveryAddress: raw.delivery_address as string | undefined,
    vatRate: asNumber(raw.vat_rate ?? raw.tax_rate ?? raw.vat_percent),
    vatAmount: asNumber(raw.vat_amount ?? raw.tax_amount ?? raw.total_tax),
    sync: raw.sync as GoodsIssue['sync'],
    sync_state: raw.sync_state as GoodsIssue['sync_state'],
    amis_sync_blocked_reason: asString(raw.amis_sync_blocked_reason),
  };
}

function withTransferWarehouses(
  line: InventoryDocLine,
  fallbackFromWarehouseId: string | undefined,
  fallbackFromWarehouseName: string | undefined,
  fallbackToWarehouseId: string | undefined,
  fallbackToWarehouseName: string | undefined,
): InventoryDocLine {
  const fromWarehouseId = line.from_warehouse_id ?? fallbackFromWarehouseId;
  const toWarehouseId = line.to_warehouse_id ?? fallbackToWarehouseId;

  return {
    ...line,
    from_warehouse_id: fromWarehouseId,
    from_warehouse_name: line.from_warehouse_name ?? fallbackFromWarehouseName,
    to_warehouse_id: toWarehouseId,
    to_warehouse_name: line.to_warehouse_name ?? fallbackToWarehouseName,
  };
}

export function normalizeStockTransferForView(raw: Record<string, unknown>): StockTransferDoc {
  const items = normalizeInventoryItems(raw);
  const lineCount = Number(raw.total_items ?? raw.totalItems ?? items.length ?? 0);
  const fromStoreId = asString(raw.from_store_id ?? raw.fromStoreId) ?? '';
  const toStoreId = asString(raw.to_store_id ?? raw.toStoreId) ?? '';
  const fromWarehouseId = asString(raw.from_warehouse_id ?? raw.fromWarehouseId);
  const toWarehouseId = asString(raw.to_warehouse_id ?? raw.toWarehouseId);
  const fromWarehouseName = asString(raw.from_warehouse_name ?? raw.fromWarehouseName) ?? undefined;
  const toWarehouseName = asString(raw.to_warehouse_name ?? raw.toWarehouseName) ?? undefined;
  const inferredScope = fromStoreId && toStoreId && fromStoreId !== toStoreId ? 'inter_branch' : 'internal';
  const inferredBusinessType =
    inferredScope === 'inter_branch' ? 'inter_branch_transfer' : 'warehouse_transfer';
  const actions = (raw.actions && typeof raw.actions === 'object'
    ? raw.actions
    : {}) as Record<string, unknown>;
  const lines = items.map((item) =>
    withTransferWarehouses(
      item,
      fromWarehouseId,
      fromWarehouseName,
      toWarehouseId,
      toWarehouseName,
    ),
  );
  const transferStatus = asString(raw.status);
  const normalizedTransferStatus: DocStatus =
    transferStatus === 'received' || transferStatus === 'verified'
      ? 'completed'
      : transferStatus === 'picking' || transferStatus === 'packed'
        ? 'approved'
        : normalizeDocStatus(transferStatus);
  const sourceWarehouseIds = new Set(
    lines.map((line) => line.from_warehouse_id).filter((value): value is string => Boolean(value)),
  );
  const destinationWarehouseIds = new Set(
    lines.map((line) => line.to_warehouse_id).filter((value): value is string => Boolean(value)),
  );
  const rawWarehouseScope = asString(raw.warehouse_scope ?? raw.warehouseScope);
  const warehouseScope: StockTransferDoc['warehouseScope'] =
    rawWarehouseScope === 'single' || rawWarehouseScope === 'multi'
      ? rawWarehouseScope
      : lines.length > 0
        ? sourceWarehouseIds.size === 1 && destinationWarehouseIds.size === 1
          ? 'single'
          : 'multi'
        : fromWarehouseId && toWarehouseId
          ? 'single'
          : 'multi';

  return {
    _id: asString(raw._id) ?? '',
    code: (raw.transfer_code ?? raw.code ?? '') as string,
    fromStoreId,
    fromStoreCode: asString(raw.from_store_code ?? raw.fromStoreCode),
    fromStoreName:
      (raw.from_store_name ?? raw.fromStoreName ?? asString(raw.from_store_id ?? raw.fromStoreId) ??
        '') as string,
    toStoreId,
    toStoreCode: asString(raw.to_store_code ?? raw.toStoreCode),
    toStoreName:
      (raw.to_store_name ?? raw.toStoreName ?? asString(raw.to_store_id ?? raw.toStoreId) ?? '') as string,
    lines,
    lineCount,
    fromWarehouseId,
    toWarehouseId,
    fromWarehouseName,
    toWarehouseName,
    warehouseScope,
    totalQty: Number(raw.total_quantity ?? raw.totalQty ?? 0),
    status: normalizedTransferStatus,
    rawStatus: transferStatus,
    createdBy: actorName(raw.created_by_name, raw.createdByName, raw.created_by, raw.createdBy),
    approvedBy:
      (raw.approved_by_name ??
        raw.approvedByName ??
        asString(raw.approved_by ?? raw.approvedBy)) as string | undefined,
    note: (raw.note) as string | undefined,
    createdAt: (raw.created_at ?? raw.createdAt ?? '') as string,
    approvedAt: (raw.approved_at ?? raw.approvedAt) as string | undefined,
    completedAt: (raw.completed_at ?? raw.completedAt ?? raw.received_at ?? raw.receivedAt) as
      | string
      | undefined,
    documentDate: (raw.document_date ?? raw.transfer_date) as string | undefined,
    accountingDate: raw.accounting_date as string | undefined,
    documentNo: (raw.document_no ?? raw.transfer_code) as string | undefined,
    carrierName: raw.carrier_name as string | undefined,
    transferScope: (raw.transfer_scope ?? raw.transferScope ?? inferredScope) as StockTransferDoc['transferScope'],
    transferBusinessType: (
      raw.transfer_business_type ??
      raw.transferBusinessType ??
      inferredBusinessType
    ) as StockTransferDoc['transferBusinessType'],
    goodsIssueId: asString(raw.goods_issue_id ?? raw.goodsIssueId),
    goodsIssueNumber: asString(raw.goods_issue_number ?? raw.goodsIssueNumber),
    goodsReceiptId: asString(raw.goods_receipt_id ?? raw.goodsReceiptId),
    goodsReceiptNumber: asString(raw.goods_receipt_number ?? raw.goodsReceiptNumber),
    journalEntryId: asString(raw.journal_entry_id ?? raw.journalEntryId),
    journalEntryNumber: asDisplayCode(raw.journal_entry_number ?? raw.journalEntryNumber ?? raw.entry_number ?? raw.entryNumber),
    accountingPostingMode: (raw.accounting_posting_mode ?? raw.accountingPostingMode) as StockTransferDoc['accountingPostingMode'],
    sync: raw.sync as StockTransferDoc['sync'],
    sync_state: raw.sync_state as StockTransferDoc['sync_state'],
    amis_sync_blocked_reason: asString(raw.amis_sync_blocked_reason),
    actionPermissions: {
      canSubmit: asBoolean(actions.can_submit ?? actions.canSubmit),
      canUpdate: asBoolean(actions.can_update ?? actions.canUpdate),
      canCancel: asBoolean(actions.can_cancel ?? actions.canCancel),
      canApproveOutbound: asBoolean(
        actions.can_approve_outbound ??
          actions.canApproveOutbound ??
          raw.can_approve_outbound ??
          raw.canApproveOutbound,
      ),
      canReceiveInbound: asBoolean(
        actions.can_receive_inbound ??
          actions.canReceiveInbound ??
          raw.can_receive_inbound ??
          raw.canReceiveInbound,
      ),
    },
  };
}

function normalizeTransfer(raw: Record<string, unknown>): StockTransferDoc {
  return normalizeStockTransferForView(raw);
}

function normalizeStockTakeStatus(raw: unknown): DocStatus {
  const status = asString(raw) ?? 'draft';
  if (status === 'in_progress' || status === 'pending_approval') return 'pending';
  if (status === 'reviewing') return 'reviewing';
  if (status === 'approved') return 'approved';
  if (status === 'completed') return 'completed';
  if (status === 'rejected') return 'rejected';
  if (status === 'cancelled') return 'cancelled';
  return 'draft';
}

export function normalizeLowStockAlert(raw: unknown): LowStockAlert {
  const item = (raw ?? {}) as Record<string, unknown>;
  const currentStock = Number(item.current_stock ?? item.available_quantity ?? item.quantity ?? 0);
  const threshold = Number(item.threshold ?? item.reorder_point ?? item.low_stock_threshold ?? 0);
  const variantId = asString(item.variant_id) ?? '';
  const storeId = asString(item.store_id ?? item.storeId);
  const warehouseId = asString(item.warehouse_id);
  const severity =
    (asString(item.severity) as LowStockAlert['severity'] | undefined) ??
    (currentStock <= 0 ? 'critical' : threshold > 0 && currentStock <= threshold ? 'warning' : 'info');

  return {
    _id: asString(item._id) ?? ([warehouseId, variantId].filter(Boolean).join(':') || variantId),
    product_id: asString(item.product_id),
    variant_id: variantId,
    store_id: storeId,
    store_code: asString(item.store_code ?? item.storeCode),
    store_name: asString(item.store_name ?? item.storeName),
    warehouse_id: warehouseId,
    warehouse_name: asString(item.warehouse_name ?? item.warehouseName),
    sku: asString(item.variant_label ?? item.variant_code ?? item.sku ?? item.product_sku) ?? '',
    variant_code: asString(item.variant_code ?? item.variantCode),
    variant_label: asString(item.variant_label ?? item.variantLabel),
    product_name: asString(item.product_name ?? item.name) ?? '',
    category: asString(item.category ?? item.category_name) ?? '',
    karat: asString(item.karat ?? item.purity) ?? '',
    current_stock: currentStock,
    threshold,
    severity,
    last_movement_at: asString(item.last_movement_at ?? item.updated_at ?? item.created_at),
  };
}

export function normalizeStockCountLine(raw: Record<string, unknown>): StockCountLine {
  const hasSystemQuantity = raw.system_quantity !== null && raw.system_quantity !== undefined;
  const systemQty = Number(raw.system_quantity ?? 0);
  const actualQtyRaw = raw.actual_quantity;
  const isLegacyUncountedZero =
    !raw.counted_at && Number(actualQtyRaw ?? 0) === 0 && Number(raw.diff_quantity ?? 0) === 0;
  const actualQty =
    actualQtyRaw === null || actualQtyRaw === undefined || isLegacyUncountedZero
      ? undefined
      : Number(actualQtyRaw);
  const diffQty = raw.diff_quantity !== null && raw.diff_quantity !== undefined
    ? Number(raw.diff_quantity)
    : hasSystemQuantity
      ? (actualQty ?? 0) - systemQty
      : 0;
  const skuCandidates = [raw.sku, raw.variant_code, raw.product_sku, raw.variant_id];
  const sku =
    skuCandidates
      .map((v) => asString(v))
      .find((v) => typeof v === 'string' && v.trim().length > 0) ?? '';

  return {
    itemId: asString(raw._id ?? raw.stock_take_item_id ?? raw.item_id),
    sku,
    productName: asString(raw.product_name ?? raw.product_id) ?? '',
    karat: asString(raw.karat ?? raw.purity) ?? '',
    systemQty,
    systemWeight: Number(raw.system_weight ?? 0),
    actualQty,
    actualWeight:
      raw.actual_weight === null || raw.actual_weight === undefined
        ? undefined
        : Number(raw.actual_weight),
    variance: diffQty,
    varianceWeight: Number(raw.diff_weight ?? 0),
    note: asString(raw.note ?? raw.diff_reason),
    locationId: asString(raw.warehouse_location_id),
    locationCode: asString(raw.warehouse_location_code),
    lotNumber: asString(raw.lot_number),
    expiryDate: asString(raw.expiry_date),
    resolution: asString(raw.resolution) as StockCountLine['resolution'],
    resolutionAccountCode: asString(raw.resolution_account_code),
    adjustmentDocumentType: asString(raw.adjustment_document_type) as StockCountLine['adjustmentDocumentType'],
    adjustmentDocumentId: asString(raw.adjustment_document_id),
  };
}

function normalizeStockCount(raw: Record<string, unknown>): StockCount {
  const itemsRaw = Array.isArray(raw.items)
    ? (raw.items as Record<string, unknown>[])
    : Array.isArray(raw.lines)
      ? (raw.lines as Record<string, unknown>[])
      : [];
  const lines = itemsRaw.map(normalizeStockCountLine);

  const beScope = asString(raw.scope);
  const scope: StockCount['scope'] =
    beScope === 'full' || beScope === 'category' || beScope === 'karat' || beScope === 'custom'
      ? beScope
      : raw.count_all
        ? 'full'
        : Array.isArray(raw.product_ids) && (raw.product_ids as unknown[]).length > 0
          ? 'custom'
          : 'full';

  const scopeValueArr = Array.isArray(raw.scope_value)
    ? (raw.scope_value as unknown[]).map((v) => asString(v) ?? '').filter(Boolean)
    : [];
  const scopeValueLabels = Array.isArray(raw.scope_value_labels)
    ? (raw.scope_value_labels as unknown[]).map((v) => asString(v) ?? '').filter(Boolean)
    : [];
  const scopeValue =
    scopeValueLabels.length > 0
      ? scopeValueLabels.join(', ')
      : scopeValueArr.length > 0
        ? scopeValueArr.join(', ')
        : Array.isArray(raw.product_ids) && (raw.product_ids as unknown[]).length > 0
          ? `${(raw.product_ids as unknown[]).length} sản phẩm`
          : undefined;

  return {
    _id: asString(raw._id ?? raw.stock_take_id) ?? '',
    code: (raw.take_code ?? raw.code ?? '') as string,
    name: asString(raw.name) || ((raw.take_code ?? raw.code ?? '') as string),
    rawStatus: asString(raw.status) ?? 'draft',
    scope,
    scopeValue,
    scopeValueLabels,
    storeId: asString(raw.store_id) ?? '',
    storeCode: asString(raw.store_code ?? raw.storeCode),
    storeName: asString(raw.store_name) ?? '',
    warehouseId: asString(raw.warehouse_id ?? raw.warehouseId),
    warehouseCode: asString(raw.warehouse_code ?? raw.warehouseCode),
    warehouseName: asString(raw.warehouse_name ?? raw.warehouseName),
    countMode: raw.count_mode === 'ad_hoc' ? 'ad_hoc' : 'scheduled',
    locationIds: Array.isArray(raw.location_ids) ? raw.location_ids.map((v) => asString(v) ?? '').filter(Boolean) : [],
    resultsHidden: raw.results_hidden === true,
    lines,
    totalItems: Number(raw.total_items ?? lines.length),
    countedItems: Number(raw.counted_items ?? lines.filter((l) => l.actualQty !== undefined).length),
    totalVarianceQty: Number(raw.total_diff_quantity ?? 0),
    totalVarianceValue: Number(raw.total_diff_value ?? 0),
    status: normalizeStockTakeStatus(raw.status),
    startedAt: (raw.take_date ?? raw.created_at ?? new Date().toISOString()) as string,
    countedAt: (raw.updated_at ?? raw.counted_at) as string | undefined,
    completedAt: (raw.completed_at ?? raw.approved_at) as string | undefined,
    staffName: asString(raw.staff_name ?? raw.created_by_name ?? raw.created_by) ?? '',
    note: asString(raw.note),
  };
}

export function useGoodsReceipts(params?: GoodsReceiptListQuery) {
  return useQuery({
    queryKey: ['inventory', 'receipts', params],
    queryFn: async ({ signal }) => {
      const page = unwrapPagedList<Record<string, unknown>>(await inventoryService.receipts.list(params, { signal }));
      return { data: page.data.map(normalizeReceipt), total: page.total };
    },
    retry: 0,
  });
}

export interface DocumentSyncOwner {
  doc_type: SyncDocType;
  ref_id: string;
}

/**
 * Các field đồng bộ AMIS mà BE gắn thêm vào response CHI TIẾT chứng từ kho (TCR-1138).
 *
 * Chúng cố ý KHÔNG đi qua các hàm `normalize*`: normalize dựng view-model dùng chung cho cả màn
 * danh sách, mà badge đồng bộ chỉ có ở màn chi tiết. Nối thẳng vào kết quả của hook chi tiết là
 * chỗ hẹp nhất — bỏ quên bước này thì khối "Trạng thái đồng bộ" vẫn hiện nhưng LUÔN nói "chưa
 * từng đồng bộ", kể cả khi chứng từ đang bị chặn có lý do hẳn hoi (đã dính một lần 02/09/2026).
 */
export type WithAmisSync<T> = T & {
  sync?: SyncBadge;
  sync_owner?: DocumentSyncOwner;
  amis_sync_blocked_reason?: string;
};

export function withAmisSync<T>(doc: T, raw: Record<string, unknown>): WithAmisSync<T> {
  const rawOwner = raw.sync_owner as Record<string, unknown> | undefined;
  const syncOwner: DocumentSyncOwner | undefined =
    rawOwner &&
    isSyncDocType(rawOwner.doc_type) &&
    typeof rawOwner.ref_id === 'string'
      ? {
          doc_type: rawOwner.doc_type,
          ref_id: rawOwner.ref_id,
        }
      : undefined;

  return {
    ...doc,
    sync: raw.sync as SyncBadge | undefined,
    sync_owner: syncOwner,
    amis_sync_blocked_reason:
      typeof raw.amis_sync_blocked_reason === 'string'
        ? raw.amis_sync_blocked_reason
        : undefined,
  };
}

export function useGoodsReceiptDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['inventory', 'receipts', id],
    queryFn: async () => {
      const data = await inventoryService.receipts.get(id!);
      const raw = unwrapItem<Record<string, unknown>>(data);
      if (!raw) throw new Error('Không tìm thấy phiếu nhập');
      return withAmisSync(normalizeReceipt(raw), raw);
    },
    enabled: !!id,
    retry: 0,
  });
}

export function useGoodsIssues(params?: GoodsIssueListQuery) {
  return useQuery({
    queryKey: ['inventory', 'issues', params],
    queryFn: async ({ signal }) => {
      const page = unwrapPagedList<Record<string, unknown>>(await inventoryService.issues.list(params, { signal }));
      return { data: page.data.map(normalizeIssue), total: page.total };
    },
    retry: 0,
  });
}

export function useGoodsIssueDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['inventory', 'issues', id],
    queryFn: async () => {
      const data = await inventoryService.issues.get(id!);
      const raw = unwrapItem<Record<string, unknown>>(data);
      if (!raw) throw new Error('Không tìm thấy phiếu xuất');
      return withAmisSync(normalizeIssue(raw), raw);
    },
    enabled: !!id,
    retry: 0,
  });
}

export function useStockTransfers(params?: StockTransferListQuery) {
  return useQuery({
    queryKey: ['inventory', 'transfers', params],
    queryFn: async ({ signal }) => {
      const page = unwrapPagedList<Record<string, unknown>>(await inventoryService.transfers.list(params, { signal }));
      return { data: page.data.map(normalizeTransfer), total: page.total };
    },
    retry: 0,
  });
}

export function useStockTransferDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['inventory', 'transfers', id],
    queryFn: async () => {
      const data = await inventoryService.transfers.get(id!);
      const raw = unwrapItem<Record<string, unknown>>(data);
      if (!raw) throw new Error('Không tìm thấy phiếu chuyển kho');
      return withAmisSync(normalizeTransfer(raw), raw);
    },
    enabled: !!id,
    retry: 0,
  });
}

export function useStockCounts(params?: StockTakeListQuery) {
  return useQuery({
    queryKey: ['inventory', 'counts', params],
    queryFn: async () => {
      const raw = unwrapList<Record<string, unknown>>(await inventoryService.stockTakes.list(params));
      return raw.map(normalizeStockCount);
    },
    retry: 0,
  });
}

export function useStockCountDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['inventory', 'counts', id],
    queryFn: async () => {
      const data = await inventoryService.stockTakes.get(id!);
      const raw = unwrapItem<Record<string, unknown>>(data);
      const doc = raw ? normalizeStockCount(raw) : null;
      if (!doc) throw new Error('Không tìm thấy đợt kiểm kê');
      return doc;
    },
    enabled: !!id,
    retry: 0,
  });
}

export interface LowStockAlertsFilters {
  search?: string;
  store_id?: string;
  warehouse_id?: string;
  category_id?: string;
}

export function useLowStockAlerts(filters: LowStockAlertsFilters = {}) {
  return useQuery({
    queryKey: ['inventory', 'alerts', filters],
    queryFn: async () => {
      const raw = unwrapList<unknown>(await inventoryService.alerts(filters));
      return raw.map(normalizeLowStockAlert);
    },
    retry: 0,
    refetchInterval: 30000,
  });
}
